import React, { useState, useCallback, useEffect, useMemo } from "react";
import Select from "react-select";
import axiosInstance from "../../config/axiosApi/axiosApi.jsx";
import APIs from "../../Constants/apis.js";
import Preloader from "../../components/CustomComponents/preLoad/preLoader.js";
import showErrorToast, {
  showSuccessToast,
} from "../../components/CustomComponents/toast/toast.js";
import DropDownStyles, {
  DropDownStylesDark,
  defaultSelectPortalProps,
} from "../staff/dropdownStyles.js";
import useDarkMode from "../../hooks/useDarkMode.js";
import useThemeColor from "../../hooks/useThemeColor.js";
import moment from "moment";
import swal from "sweetalert";
import "./UpdateCompany.css";

function toMonths(duration) {
  const d = parseInt(duration, 10) || 1;
  if (d === 30 || d === 31) return 1;
  if (d === 365 || d === 366) return 12;
  if (d > 24) return Math.max(1, Math.round(d / 30));
  return d;
}

function getPrimaryPrice(pkg) {
  const prices = Array.isArray(pkg?.prices) ? pkg.prices : [];
  return prices[0] || null;
}

function formatIncludes(pkg) {
  const ents = Array.isArray(pkg?.entitlements) ? pkg.entitlements : [];
  if (ents.length === 0) return "";
  return ents
    .map((e) => {
      const type = e.entitlementType || e.EntitlementType || "";
      const title = (e.entitlementTitle || e.EntitlementTitle || "").trim();
      const qty = e.quantity ?? e.Quantity ?? 0;
      const price = parseFloat(e.unitPrice ?? e.UnitPrice ?? 0).toFixed(2);
      if (title) return `${qty} ${title} (£${price})`;
      if (type === "SaaS") return `${qty} mo SaaS (£${price})`;
      if (type === "SmsSegments") return `${qty} SMS (£${price})`;
      if (type === "Hardware") return `${qty} Hardware (£${price})`;
      return `${type} ×${qty} (£${price})`;
    })
    .join("; ");
}

const CompanySubscriptions = ({ CompID }) => {
  const isDarkMode = useDarkMode();
  const themeColor = useThemeColor();
  const dropdownStyles = isDarkMode ? DropDownStylesDark : DropDownStyles;

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [subscriptions, setSubscriptions] = useState([]);
  const [packages, setPackages] = useState([]);
  const [showAssignForm, setShowAssignForm] = useState(false);

  const [selectedPackages, setSelectedPackages] = useState([]);
  const [customLines, setCustomLines] = useState([]);
  const [startDate, setStartDate] = useState(moment().format("YYYY-MM-DD"));
  const [isAutoRenewal, setIsAutoRenewal] = useState(true);
  const [discountPercent, setDiscountPercent] = useState("");
  const [discountAmount, setDiscountAmount] = useState("");
  const [discountLabel, setDiscountLabel] = useState("");
  const [lastCreatedInvoice, setLastCreatedInvoice] = useState(null);
  const [cancellingId, setCancellingId] = useState(null);

  const fetchSubscriptions = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get(APIs.GET_COMPANY_SUBSCRIPTIONS, {
        params: { compID: CompID },
      });
      if (response.data.status && Array.isArray(response.data.data)) {
        setSubscriptions(response.data.data);
      } else {
        setSubscriptions([]);
      }
      setLoading(false);
    } catch (error) {
      console.error("Error fetching subscriptions:", error);
      setLoading(false);
      showErrorToast("Failed to load subscription data");
    }
  }, [CompID]);

  const fetchPackages = useCallback(async () => {
    try {
      const response = await axiosInstance.get(APIs.GET_ALL_PACKAGES_WITH_PRICES);
      if (response.data.status && Array.isArray(response.data.data)) {
        setPackages(response.data.data);
      } else {
        setPackages([]);
      }
    } catch (error) {
      console.error("Error fetching packages:", error);
      setPackages([]);
    }
  }, []);

  useEffect(() => {
    if (CompID) {
      fetchSubscriptions();
      fetchPackages();
    }
  }, [CompID, fetchSubscriptions, fetchPackages]);

  const packageOptions = useMemo(() => {
    return packages
      .filter((pkg) => pkg.pStatus !== false)
      .map((pkg) => {
        const price = getPrimaryPrice(pkg);
        const months = price ? toMonths(price.duration) : 1;
        const amount = price ? parseFloat(price.price || 0).toFixed(2) : "0.00";
        const includes = formatIncludes(pkg);
        return {
          value: pkg.id,
          label: includes
            ? `${pkg.pName} — £${amount} / ${months} mo (${includes})`
            : `${pkg.pName} — £${amount} / ${months} mo`,
          package: pkg,
          price,
          months,
        };
      })
      .filter((opt) => opt.price);
  }, [packages]);

  const assignPreview = useMemo(() => {
    let net = 0;
    let vat = 0;
    selectedPackages.forEach((opt) => {
      const price = parseFloat(opt.price?.price || 0);
      const vatPercent = parseFloat(opt.package?.pVatPercent ?? 0);
      const lineVat = Math.round(price * (vatPercent / 100) * 100) / 100;
      net += price;
      vat += lineVat;
    });
    customLines.forEach((line) => {
      const qty = Math.max(1, parseInt(line.quantity, 10) || 1);
      const lineNet = Math.round((parseFloat(line.netAmount) || 0) * qty * 100) / 100;
      const vatPercent = Math.min(100, Math.max(0, parseFloat(line.vatPercent) || 0));
      const lineVat = Math.round(lineNet * (vatPercent / 100) * 100) / 100;
      net += lineNet;
      vat += lineVat;
    });
    net = Math.round(net * 100) / 100;
    vat = Math.round(vat * 100) / 100;
    const gross = Math.round((net + vat) * 100) / 100;
    const pct = Math.min(100, Math.max(0, parseFloat(discountPercent) || 0));
    let discount = 0;
    if (pct > 0) {
      discount = Math.round(gross * (pct / 100) * 100) / 100;
    } else {
      discount = Math.max(0, parseFloat(discountAmount) || 0);
    }
    if (discount > gross) discount = gross;
    return {
      net,
      vat,
      gross,
      discount,
      discountPercent: pct,
      total: Math.round((gross - discount) * 100) / 100,
    };
  }, [selectedPackages, customLines, discountPercent, discountAmount]);

  const resetAssignForm = () => {
    setSelectedPackages([]);
    setCustomLines([]);
    setStartDate(moment().format("YYYY-MM-DD"));
    setIsAutoRenewal(true);
    setDiscountPercent("");
    setDiscountAmount("");
    setDiscountLabel("");
  };

  const addCustomLine = () => {
    setCustomLines((prev) => [
      ...prev,
      { description: "", netAmount: "", vatPercent: "20", quantity: 1 },
    ]);
  };

  const updateCustomLine = (index, field, value) => {
    setCustomLines((prev) =>
      prev.map((row, i) => (i === index ? { ...row, [field]: value } : row))
    );
  };

  const removeCustomLine = (index) => {
    setCustomLines((prev) => prev.filter((_, i) => i !== index));
  };

  const handleAssignPackages = async () => {
    if (selectedPackages.length === 0 && customLines.length === 0) {
      showErrorToast("Select at least one package or add a custom bill line.");
      return;
    }

    if (!startDate && selectedPackages.length > 0) {
      showErrorToast("Please select a start date.");
      return;
    }

    for (const line of customLines) {
      if (!(line.description || "").trim()) {
        showErrorToast("Custom bill description is required.");
        return;
      }
      if ((parseFloat(line.netAmount) || 0) <= 0) {
        showErrorToast("Custom bill amount must be greater than zero.");
        return;
      }
    }

    try {
      setSaving(true);

      const payload = {
        CompID: parseInt(CompID, 10),
        StartDate: startDate || moment().format("YYYY-MM-DD"),
        IsAutoRenewal: isAutoRenewal,
        PackageSelections: selectedPackages.map((p) => ({ PackageID: p.value })),
        CustomLines: customLines.map((c) => ({
          Description: (c.description || "").trim(),
          NetAmount: parseFloat(c.netAmount) || 0,
          VatPercent: parseFloat(c.vatPercent) || 0,
          Quantity: Math.max(1, parseInt(c.quantity, 10) || 1),
        })),
        DiscountPercent: Math.min(100, Math.max(0, parseFloat(discountPercent) || 0)),
        DiscountAmount:
          (parseFloat(discountPercent) || 0) > 0
            ? 0
            : Math.max(0, parseFloat(discountAmount) || 0),
        DiscountLabel: (discountLabel || "").trim(),
      };

      const response = await axiosInstance.post(
        APIs.POST_ASSIGN_PACKAGES_WITH_INVOICE,
        payload
      );

      if (response.data.status) {
        const data = response.data.data || {};
        const invoice = data.invoice || data.Invoice || null;
        setLastCreatedInvoice(invoice);
        const invNo = invoice?.invoiceNumber || invoice?.InvoiceNumber || "";
        const total =
          invoice?.totalAmount ?? invoice?.TotalAmount ?? assignPreview.total;
        showSuccessToast(
          invNo
            ? `Assigned. Invoice ${invNo} pending (£${parseFloat(total).toFixed(2)} incl. VAT). Access / SMS update when paid.`
            : "Packages assigned and invoice created."
        );
        resetAssignForm();
        setShowAssignForm(false);
        await fetchSubscriptions();
      } else {
        showErrorToast(response.data.message || "Failed to assign packages");
      }
    } catch (error) {
      console.error(error);
      showErrorToast(
        error.response?.data?.message || "Failed to assign packages"
      );
    } finally {
      setSaving(false);
    }
  };

  const handleCancelSubscription = async (sub) => {
    const id = sub.subscriptionID ?? sub.SubscriptionID;
    const pkgId = sub.packageID ?? sub.PackageID;
    const pkg = packages.find((p) => p.id === pkgId);
    const name = pkg?.pName || `Package #${pkgId}`;
    const status = (sub.status ?? sub.Status ?? "").toLowerCase();
    if (!id || !["active", "trial", "suspended"].includes(status)) {
      showErrorToast("Only active subscriptions can be cancelled.");
      return;
    }

    const confirmed = await swal({
      title: "Remove this package?",
      text: `Cancel "${name}" (#${id}) for this company? Company access will use the latest end date of any remaining active packages. SMS/license entitlements already granted are not reversed.`,
      icon: "warning",
      buttons: ["Keep", "Cancel package"],
      dangerMode: true,
    });
    if (!confirmed) return;

    try {
      setCancellingId(id);
      const response = await axiosInstance.post(
        APIs.POST_CANCEL_SUBSCRIPTION,
        null,
        {
          params: {
            subscriptionID: id,
            reason: "Cancelled by SuperAdmin",
          },
        }
      );
      if (response.data.status) {
        showSuccessToast(`"${name}" cancelled`);
        await fetchSubscriptions();
      } else {
        showErrorToast(response.data.message || "Failed to cancel subscription");
      }
    } catch (error) {
      console.error(error);
      showErrorToast(
        error.response?.data?.message || "Failed to cancel subscription"
      );
    } finally {
      setCancellingId(null);
    }
  };

  const getStatusBadge = (status) => {
    const statusMap = {
      Active: { color: "success", text: "Active", bg: "rgba(25, 135, 84, 0.12)" },
      Expired: { color: "danger", text: "Expired", bg: "rgba(220, 53, 69, 0.12)" },
      Cancelled: {
        color: "secondary",
        text: "Cancelled",
        bg: "rgba(108, 117, 125, 0.12)",
      },
      Suspended: {
        color: "warning",
        text: "Suspended",
        bg: "rgba(255, 193, 7, 0.18)",
      },
    };
    const statusInfo =
      statusMap[status] || {
        color: "secondary",
        text: status,
        bg: "rgba(108, 117, 125, 0.12)",
      };
    return (
      <span
        className={`uc-badge-pill text-${statusInfo.color}`}
        style={{ background: statusInfo.bg }}
      >
        {statusInfo.text}
      </span>
    );
  };

  const actionButtonStyle = {
    backgroundColor: themeColor,
    background: `linear-gradient(135deg, ${themeColor} 0%, ${themeColor}dd 100%)`,
    border: "none",
    color: "white",
    borderRadius: "8px",
    padding: "8px 16px",
    fontSize: "14px",
    fontWeight: "600",
    boxShadow: `0 2px 8px ${themeColor}40`,
  };

  if (loading) {
    return <Preloader />;
  }

  return (
    <>
      <section className="uc-section uc-section--nested">
        <div className="uc-section__header d-flex justify-content-between align-items-start flex-wrap gap-2">
          <div>
            <h5>Company Subscriptions</h5>
            <p>Multiple active packages are supported. Access / entitlements apply when invoices are paid.</p>
          </div>
          <button
            type="button"
            className="btn"
            style={actionButtonStyle}
            onClick={() => {
              setShowAssignForm((prev) => !prev);
              if (!showAssignForm) resetAssignForm();
            }}
          >
            <i className={`fa ${showAssignForm ? "fa-times" : "fa-plus"} me-2`} />
            {showAssignForm ? "Cancel" : "Assign packages"}
          </button>
        </div>
        <div className="uc-section__body">
          {subscriptions.length === 0 ? (
            <div className="uc-empty">
              <i className="fa fa-credit-card"></i>
              No subscriptions found for this company.
            </div>
          ) : (
            <div className="table-responsive">
              <table className="table table-hover">
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>Package</th>
                    <th>Status</th>
                    <th>Start</th>
                    <th>End</th>
                    <th>Price</th>
                    <th>Auto renew</th>
                    <th className="text-center">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {subscriptions.map((sub) => {
                    const id = sub.subscriptionID ?? sub.SubscriptionID;
                    const pkgId = sub.packageID ?? sub.PackageID;
                    const pkg = packages.find((p) => p.id === pkgId);
                    const status = (sub.status ?? sub.Status ?? "").toLowerCase();
                    const canCancel = ["active", "trial", "suspended"].includes(status);
                    return (
                      <tr key={id}>
                        <td>#{id}</td>
                        <td>
                          {pkg?.pName || `Package #${pkgId}`}
                          {pkg && formatIncludes(pkg) ? (
                            <div className="small text-muted">{formatIncludes(pkg)}</div>
                          ) : null}
                        </td>
                        <td>{getStatusBadge(sub.status ?? sub.Status)}</td>
                        <td>
                          {moment(sub.startDate ?? sub.StartDate).format("MMM DD, YYYY")}
                        </td>
                        <td>
                          {moment(sub.endDate ?? sub.EndDate).format("MMM DD, YYYY")}
                        </td>
                        <td>
                          £
                          {parseFloat(
                            sub.subscriptionPrice ?? sub.SubscriptionPrice ?? 0
                          ).toFixed(2)}
                        </td>
                        <td>
                          {(sub.isAutoRenewal ?? sub.IsAutoRenewal) ? "Yes" : "No"}
                        </td>
                        <td className="text-center">
                          {canCancel ? (
                            <button
                              type="button"
                              className="btn btn-sm btn-outline-danger"
                              title="Cancel / remove package"
                              disabled={cancellingId === id}
                              onClick={() => handleCancelSubscription(sub)}
                            >
                              {cancellingId === id ? (
                                <i className="fa fa-spinner fa-spin" />
                              ) : (
                                <>
                                  <i className="fa fa-times me-1" />
                                  Cancel
                                </>
                              )}
                            </button>
                          ) : (
                            <span className="text-muted small">—</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </section>

      {showAssignForm && (
        <section className="uc-section uc-section--nested">
          <div className="uc-section__header">
            <h5>Assign packages / custom bills</h5>
            <p>
              Select one or more packages and optional custom bill lines. Creates one
              multi-line pending invoice. Existing subscriptions are kept (added alongside).
            </p>
          </div>
          <div className="uc-section__body">
            {packageOptions.length === 0 ? (
              <div className="alert alert-warning mb-3">
                No active packages with pricing found. Create a package with entitlements first.
              </div>
            ) : (
              <div className="row g-3 mb-3">
                <div className="col-md-8">
                  <label className="form-label">Packages</label>
                  <Select
                    {...defaultSelectPortalProps}
                    isMulti
                    options={packageOptions}
                    value={selectedPackages}
                    onChange={(val) => setSelectedPackages(val || [])}
                    placeholder="Select one or more packages..."
                    styles={dropdownStyles}
                  />
                </div>
                <div className="col-md-4">
                  <label className="form-label">Start Date</label>
                  <input
                    type="date"
                    className="form-control"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                  />
                </div>
              </div>
            )}

            <div className="d-flex justify-content-between align-items-center mb-2">
              <label className="form-label mb-0">Custom customer bills</label>
              <button type="button" className="btn btn-sm btn-outline-secondary" onClick={addCustomLine}>
                <i className="fa fa-plus me-1" />
                Add bill line
              </button>
            </div>
            {customLines.length > 0 && (
              <div className="table-responsive mb-3">
                <table className="table table-sm">
                  <thead>
                    <tr>
                      <th>Description</th>
                      <th>Net (£)</th>
                      <th>VAT %</th>
                      <th>Qty</th>
                      <th />
                    </tr>
                  </thead>
                  <tbody>
                    {customLines.map((line, idx) => (
                      <tr key={idx}>
                        <td>
                          <input
                            className="form-control form-control-sm"
                            value={line.description}
                            onChange={(e) =>
                              updateCustomLine(idx, "description", e.target.value)
                            }
                          />
                        </td>
                        <td style={{ width: 110 }}>
                          <input
                            type="number"
                            className="form-control form-control-sm"
                            step="0.01"
                            min="0"
                            value={line.netAmount}
                            onChange={(e) =>
                              updateCustomLine(idx, "netAmount", e.target.value)
                            }
                          />
                        </td>
                        <td style={{ width: 90 }}>
                          <input
                            type="number"
                            className="form-control form-control-sm"
                            step="0.01"
                            min="0"
                            max="100"
                            value={line.vatPercent}
                            onChange={(e) =>
                              updateCustomLine(idx, "vatPercent", e.target.value)
                            }
                          />
                        </td>
                        <td style={{ width: 70 }}>
                          <input
                            type="number"
                            className="form-control form-control-sm"
                            min="1"
                            value={line.quantity}
                            onChange={(e) =>
                              updateCustomLine(idx, "quantity", e.target.value)
                            }
                          />
                        </td>
                        <td>
                          <button
                            type="button"
                            className="btn btn-sm btn-outline-danger"
                            onClick={() => removeCustomLine(idx)}
                          >
                            <i className="fa fa-trash" />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            <div className="row g-3 mb-3">
              <div className="col-md-4">
                <label className="form-label">Discount label</label>
                <input
                  type="text"
                  className="form-control"
                  placeholder="e.g. Super Discount"
                  value={discountLabel}
                  onChange={(e) => setDiscountLabel(e.target.value)}
                />
              </div>
              <div className="col-md-4">
                <label className="form-label">Discount %</label>
                <input
                  type="number"
                  className="form-control"
                  min="0"
                  max="100"
                  step="0.01"
                  placeholder="0"
                  value={discountPercent}
                  onChange={(e) => {
                    setDiscountPercent(e.target.value);
                    if (e.target.value) setDiscountAmount("");
                  }}
                />
                <small className="text-muted">% off subtotal + VAT</small>
              </div>
              <div className="col-md-4">
                <label className="form-label">Or fixed discount (£)</label>
                <input
                  type="number"
                  className="form-control"
                  min="0"
                  step="0.01"
                  placeholder="0.00"
                  disabled={(parseFloat(discountPercent) || 0) > 0}
                  value={discountAmount}
                  onChange={(e) => setDiscountAmount(e.target.value)}
                />
              </div>
            </div>

            <div className="uc-meta-grid mb-3">
              <div className="uc-meta-item">
                <span className="uc-meta-item__label">Net (ex-VAT)</span>
                <div className="uc-meta-item__value">£{assignPreview.net.toFixed(2)}</div>
              </div>
              <div className="uc-meta-item">
                <span className="uc-meta-item__label">VAT</span>
                <div className="uc-meta-item__value">£{assignPreview.vat.toFixed(2)}</div>
              </div>
              <div className="uc-meta-item">
                <span className="uc-meta-item__label">
                  Discount
                  {assignPreview.discountPercent > 0
                    ? ` (${assignPreview.discountPercent}%)`
                    : ""}
                </span>
                <div className="uc-meta-item__value text-danger">
                  −£{assignPreview.discount.toFixed(2)}
                </div>
              </div>
              <div className="uc-meta-item">
                <span className="uc-meta-item__label">Invoice total</span>
                <div className="uc-meta-item__value text-success fw-bold">
                  £{assignPreview.total.toFixed(2)}
                  {assignPreview.discount > 0 ? (
                    <span className="text-muted text-decoration-line-through ms-2 small fw-normal">
                      £{assignPreview.gross.toFixed(2)}
                    </span>
                  ) : null}
                </div>
              </div>
            </div>

            {lastCreatedInvoice && (
              <div className="alert alert-info mb-3">
                Pending invoice{" "}
                <strong>
                  {lastCreatedInvoice.invoiceNumber || lastCreatedInvoice.InvoiceNumber}
                </strong>
                : £
                {parseFloat(
                  lastCreatedInvoice.totalAmount ?? lastCreatedInvoice.TotalAmount ?? 0
                ).toFixed(2)}{" "}
                (incl. VAT).
              </div>
            )}

            <div className="form-check mb-3">
              <input
                className="form-check-input"
                type="checkbox"
                id="isAutoRenewal"
                checked={isAutoRenewal}
                onChange={(e) => setIsAutoRenewal(e.target.checked)}
              />
              <label className="form-check-label" htmlFor="isAutoRenewal">
                Auto renewal (for package subscriptions)
              </label>
            </div>

            <button
              type="button"
              className="btn"
              style={actionButtonStyle}
              onClick={handleAssignPackages}
              disabled={
                saving ||
                (selectedPackages.length === 0 && customLines.length === 0)
              }
            >
              {saving ? (
                <>
                  <i className="fa fa-spinner fa-spin me-2" />
                  Creating invoice...
                </>
              ) : (
                <>
                  <i className="fa fa-check me-2" />
                  Create multi-bill invoice
                </>
              )}
            </button>
          </div>
        </section>
      )}
    </>
  );
};

export default CompanySubscriptions;
